import React, { useEffect, useState } from 'react';

// Classic Tetris block definitions with modern colors and effects
const tetrisBlockDefs = [
  {
    name: 'I',
    color: '#00FFFF',
    gradient: '#00FFFF',
    shadow: '#00B8B8',
    shape: [
      [1, 1, 1, 1]
    ]
  },
  {
    name: 'O',
    color: '#FFFF00',
    gradient: '#FFFF00',
    shadow: '#FFB800',
    shape: [
      [1, 1],
      [1, 1]
    ]
  },
  {
    name: 'T',
    color: '#FF00FF',
    gradient: '#FF00FF',
    shadow: '#B800B8',
    shape: [
      [0, 1, 0],
      [1, 1, 1]
    ]
  },
  {
    name: 'S',
    color: '#00FF00',
    gradient: '#00FF00',
    shadow: '#00B800',
    shape: [
      [0, 1, 1],
      [1, 1, 0]
    ]
  },
  {
    name: 'Z',
    color: '#FF0000',
    gradient: '#FF0000',
    shadow: '#B80000',
    shape: [
      [1, 1, 0],
      [0, 1, 1]
    ]
  },
  {
    name: 'J',
    color: '#0000FF',
    gradient: '#0000FF',
    shadow: '#0000B8',
    shape: [
      [1, 0, 0],
      [1, 1, 1]
    ]
  },
  {
    name: 'L',
    color: '#FF8000',
    gradient: '#FF8000',
    shadow: '#B86000',
    shape: [
      [0, 0, 1],
      [1, 1, 1]
    ]
  }
];

// Helper to rotate a 2D array 90deg clockwise
function rotate(shape: number[][], times: number): number[][] {
  let s = shape;
  for (let t = 0; t < times; t++) {
    s = s[0].map((_: number, i: number) => s.map((row: number[]) => row[i]).reverse());
  }
  return s;
}

const TetrisBackground: React.FC = () => {
  const [dimensions, setDimensions] = useState({
    cellSize: 64, // 4rem = 64px
    svgWidth: 1600,
    svgHeight: 900
  });

  useEffect(() => {
    const updateDimensions = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const isMobile = width < 768;
      const isTablet = width >= 768 && width < 1024;
      
      // Calculate cell size based on screen width
      const baseCellSize = isMobile ? 32 : isTablet ? 48 : 64; // 2rem, 3rem, 4rem
      
      setDimensions({
        cellSize: baseCellSize,
        svgWidth: width,
        svgHeight: height
      });
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Generate grid cells with Tetris pieces
  const gridCells = [];
  const numCols = Math.ceil(dimensions.svgWidth / dimensions.cellSize);
  const numRows = Math.ceil(dimensions.svgHeight / dimensions.cellSize);

  // Create a grid of cells
  const grid: (number | null)[][] = Array(numRows).fill(null).map(() => Array(numCols).fill(null));

  // Place Tetris pieces in the grid
  for (let row = 0; row < numRows; row += 2) {
    for (let col = 0; col < numCols; col += 2) {
      if (grid[row][col] === null) {
        const def = tetrisBlockDefs[Math.floor(Math.random() * tetrisBlockDefs.length)];
        const rotation = Math.floor(Math.random() * 4);
        const shape = rotate(def.shape, rotation);
        
        // Check if we can place the piece
        let canPlace = true;
        for (let r = 0; r < shape.length; r++) {
          for (let c = 0; c < shape[0].length; c++) {
            if (shape[r][c] && (row + r >= numRows || col + c >= numCols || grid[row + r][col + c] !== null)) {
              canPlace = false;
              break;
            }
          }
          if (!canPlace) break;
        }

        if (canPlace) {
          // Place the piece
          for (let r = 0; r < shape.length; r++) {
            for (let c = 0; c < shape[0].length; c++) {
              if (shape[r][c]) {
                grid[row + r][col + c] = 1;
                gridCells.push({
                  row: row + r,
                  col: col + c,
                  color: def.color,
                  opacity: 0.3
                });
              }
            }
          }
        }
      }
    }
  }

  return (
    <div 
      className="fixed inset-0 w-full h-full z-0 pointer-events-none select-none bg-white"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${numCols}, ${dimensions.cellSize}px)`,
        gridTemplateRows: `repeat(${numRows}, ${dimensions.cellSize}px)`,
        background: '#ffffff'
      }}
    >
      {/* Grid lines */}
      {Array.from({ length: numCols + 1 }).map((_, i) => (
        <div
          key={`v-line-${i}`}
          style={{
            position: 'absolute',
            left: `${i * dimensions.cellSize}px`,
            top: 0,
            bottom: 0,
            width: '1px',
            background: '#e0e0e0',
            zIndex: 1
          }}
        />
      ))}
      {Array.from({ length: numRows + 1 }).map((_, i) => (
        <div
          key={`h-line-${i}`}
          style={{
            position: 'absolute',
            top: `${i * dimensions.cellSize}px`,
            left: 0,
            right: 0,
            height: '1px',
            background: '#e0e0e0',
            zIndex: 1
          }}
        />
      ))}

      {/* Tetris pieces */}
      {gridCells.map((cell, index) => (
        <div
          key={`grid-${index}`}
          style={{
            gridRow: cell.row + 1,
            gridColumn: cell.col + 1,
            background: cell.color,
            opacity: 0.3,
            borderRadius: '2px',
            zIndex: 2,
            width: '100%',
            height: '100%'
          }}
        />
      ))}
    </div>
  );
};

export default TetrisBackground; 